import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Select, Spin } from "antd";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { LookupItem } from "./LookupModal";
import { LookupModal } from "./LookupModal";

type SelectOpt = { value: number | string; label: React.ReactNode; isAddNew?: boolean };

const itemToOpt = (x: LookupItem): SelectOpt => ({
  value: x.id,
  label: x.subtitle ? `${x.label} — ${x.subtitle}` : x.label,
});

export interface LookupSelectProps {
  value?: number;
  onChange?: (value: number | undefined) => void;
  placeholder?: string;
  fetcher: (q: string, page: number, pageSize: number) => Promise<{ items: LookupItem[]; total: number }>;
  modalTitle?: string;
  searchPlaceholder?: string;
  /** Called when the user confirms "Add New". Receives the current search text
   *  so the parent can pre-fill the creation form. */
  onAddNew?: (searchText: string) => void;
  /** @deprecated Use onAddNew with an inline modal instead of navigating away. */
  addNewPath?: string;
  /** Entity label shown in the "Add …" hint, e.g. "Party", "Branch". */
  addNewLabel?: string;
  /** Extra options to always show (e.g. a freshly-created record before the
   *  next server fetch).  Merged at the front, deduplicated by id.          */
  seededOptions?: LookupItem[];
  allowClear?: boolean;
  autoFocus?: boolean;
  style?: React.CSSProperties;
  disabled?: boolean;
}

export const LookupSelect = ({
  value,
  onChange,
  placeholder = "Search...",
  fetcher,
  modalTitle = "Select",
  searchPlaceholder,
  onAddNew,
  addNewPath,
  disabled = false,
  addNewLabel,
  seededOptions,
  allowClear = false,
  autoFocus = false,
  style,
}: LookupSelectProps) => {
  const navigate = useNavigate();
  const selectRef = useRef<any>(null);
  const [opts, setOpts] = useState<SelectOpt[]>(
    () => (seededOptions ?? []).map(itemToOpt)
  );
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [searchText, setSearchText] = useState("");

  // When parent injects newly-created options (e.g. after inline branch create),
  // merge them in immediately so the selected label renders correctly.
  useEffect(() => {
    if (!seededOptions?.length) return;
    const seededIds = new Set(seededOptions.map(s => s.id));
    setOpts(prev => [
      ...seededOptions.map(itemToOpt),
      ...prev.filter(x => !seededIds.has(x.value as number) && !x.isAddNew),
    ]);
  }, [seededOptions]);

  const canAddNew = !!(onAddNew || addNewPath);

  const triggerAddNew = () => {
    if (onAddNew) {
      onAddNew(searchText.trim());
    } else if (addNewPath) {
      const q = searchText.trim();
      navigate(q ? `${addNewPath}?q=${encodeURIComponent(q)}` : addNewPath);
    }
  };

  const search = async (q: string) => {
    setSearchText(q);
    setLoading(true);
    try {
      const r = await fetcher(q, 1, 20);
      const fetchedItems = r.items ?? [];
      const seededIds = new Set((seededOptions ?? []).map(s => s.id));
      setOpts([
        ...(seededOptions ?? []).map(itemToOpt),
        ...fetchedItems.filter(x => !seededIds.has(x.id)).map(itemToOpt),
      ]);
    } catch { /* silent */ } finally { setLoading(false); }
  };

  const handleModalSelect = (item: LookupItem) => {
    const opt = itemToOpt(item);
    setOpts(prev => [...prev.filter(x => x.value !== item.id), opt]);
    onChange?.(item.id);
    setModalOpen(false);
  };

  // --- notFoundContent: shown when search returns 0 results ---
  const noResultsContent = loading ? (
    <div style={{ padding: "8px 12px", textAlign: "center" }}>
      <Spin size="small" />
    </div>
  ) : searchText.trim() && canAddNew ? (
    <div style={{ padding: "4px 8px" }}>
      <Button
        type="link"
        size="small"
        icon={<PlusOutlined />}
        style={{ color: "var(--primary, #1677ff)", padding: 0, height: "auto" }}
        onMouseDown={(e) => e.preventDefault()}
        onClick={triggerAddNew}
      >
        {addNewLabel ? `Add ${addNewLabel} "${searchText.trim()}"` : `Add "${searchText.trim()}"`}
      </Button>
      <div style={{ fontSize: 11, opacity: 0.55, marginTop: 2, paddingLeft: 2 }}>
        or press <kbd style={{ padding: "0 4px", border: "1px solid currentColor", borderRadius: 3, fontSize: 10 }}>Enter</kbd>
      </div>
    </div>
  ) : searchText.trim() ? (
    <div style={{ padding: "6px 12px", opacity: 0.6, fontSize: 12 }}>No results found</div>
  ) : undefined;  // show default AntD "No data"

  return (
    <>
      <Select
        disabled={disabled}
        ref={selectRef}
        showSearch
        filterOption={false}
        autoFocus={autoFocus}
        value={value}
        onChange={(v) => onChange?.(v as number | undefined)}
        placeholder={placeholder}
        loading={loading}
        options={opts as any}
        onSearch={(q) => void search(q)}
        onDropdownVisibleChange={(vis) => {
          if (vis && opts.length === 0) void search("");
          if (!vis) setSearchText("");
        }}
        onKeyDown={(e) => {
          // Enter with zero results → trigger Add New
          if (
            e.key === "Enter"
            && canAddNew
            && searchText.trim()
            && opts.filter(o => !o.isAddNew).length === 0
            && !loading
          ) {
            e.preventDefault();
            e.stopPropagation();
            triggerAddNew();
          }
        }}
        notFoundContent={noResultsContent}
        allowClear={allowClear}
        style={{ width: "100%", ...style }}
        dropdownRender={(menu) => (
          <>
            {menu}
            <div style={{ padding: "6px 8px", display: "flex", gap: 6, borderTop: "1px solid var(--app-bg)" }}>
              <Button
                size="small"
                icon={<SearchOutlined />}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setModalOpen(true)}
              >
                Browse
              </Button>
              {canAddNew && (
                <Button
                  size="small"
                  icon={<PlusOutlined />}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={triggerAddNew}
                >
                  Add New
                </Button>
              )}
            </div>
          </>
        )}
      />
      <LookupModal
        title={modalTitle}
        open={modalOpen}
        fetcher={fetcher}
        onClose={() => setModalOpen(false)}
        onSelect={handleModalSelect}
        onAddNew={onAddNew ? () => onAddNew(searchText.trim()) : undefined}
        searchPlaceholder={searchPlaceholder}
      />
    </>
  );
};
